import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Anchor, Group, Text, Tooltip } from "@mantine/core";
import type { StoreRecord } from "@/lib/queries";
import {
  storeDaySchedule,
  shiftSegments,
  totalOpenMinutes,
  type DaySchedule,
  type Segment,
} from "@/lib/schedule";
import {
  minutesOfDayIn,
  shiftBetween,
  weekdayIn,
  formatMinutes,
} from "@/lib/timezone";

/**
 * The zone the horizontal axis is drawn in, or `null` to give every row its
 * own store-local clock.
 */
export type AxisZone = string | null;

const LABEL_WIDTH = 220;
const ROW_HEIGHT = 26;
/** A gridline and label every three hours keeps 24 columns readable. */
const TICK_HOURS = 3;

function pct(minutes: number): string {
  return `${(minutes / 1440) * 100}%`;
}

interface HoursTimelineProps {
  stores: StoreRecord[];
  /** Weekday being shown, lowercase, e.g. `"tuesday"`. */
  day: string;
  /** A zone name to draw every row in, or `null` for each store's own clock. */
  axisZone: AxisZone;
  /** Re-supplied by the page on a timer so the now-line advances. */
  now: Date;
}

/**
 * Where a tick label sits relative to its mark.
 *
 * The 00 and 24 labels would overhang the grid if centred, so they anchor to
 * the edge they sit on; everything between centres on its own mark.
 */
function tickTransform(hour: number): string {
  if (hour === 0) return "none";
  if (hour === 24) return "translateX(-100%)";
  return "translateX(-50%)";
}

export function HoursTimeline({
  stores,
  day,
  axisZone,
  now,
}: HoursTimelineProps) {
  const ticks = Array.from(
    { length: 24 / TICK_HOURS + 1 },
    (_, index) => index * TICK_HOURS,
  );

  // On a shared axis every row's now-line sits at the same offset, so the
  // per-row lines join into one continuous rule down the grid.
  const axisWeekday = axisZone ? weekdayIn(axisZone, now) : null;
  const axisNowMin = axisZone ? minutesOfDayIn(axisZone, now) : null;

  return (
    <div style={{ minWidth: 760 }}>
      <div style={{ display: "flex", paddingBottom: 4 }}>
        <div style={{ width: LABEL_WIDTH, flexShrink: 0 }} />
        <div style={{ position: "relative", flex: 1, height: 16 }}>
          {ticks.map((hour) => (
            <Text
              key={hour}
              size="xs"
              c="dimmed"
              style={{
                position: "absolute",
                left: pct(hour * 60),
                transform: tickTransform(hour),
                whiteSpace: "nowrap",
              }}
            >
              {String(hour).padStart(2, "0")}
            </Text>
          ))}
        </div>
      </div>

      {stores.map((store) => (
        <StoreRow
          key={store.id}
          store={store}
          day={day}
          axisZone={axisZone}
          now={now}
          ticks={ticks}
          axisWeekday={axisWeekday}
          axisNowMin={axisNowMin}
        />
      ))}
    </div>
  );
}

interface StoreRowProps {
  store: StoreRecord;
  day: string;
  axisZone: AxisZone;
  now: Date;
  ticks: number[];
  axisWeekday: string | null;
  axisNowMin: number | null;
}

function StoreRow({
  store,
  day,
  axisZone,
  now,
  ticks,
  axisWeekday,
  axisNowMin,
}: StoreRowProps) {
  const schedule = storeDaySchedule(store, day);

  const shift = axisZone ? shiftBetween(store.timezone, axisZone, now) : 0;
  // A store whose zone the runtime cannot parse gets no bars on a shared axis,
  // rather than bars placed at the wrong hour.
  const placeable = shift !== null;
  const segments: Segment[] =
    schedule.kind === "windows" && placeable
      ? shiftSegments(schedule.segments, shift)
      : [];

  // "Now" is only meaningful on the row's own today: on a shared axis that is
  // the axis zone's weekday, otherwise the store's own.
  let nowMin: number | null;
  if (axisZone) {
    nowMin = axisWeekday === day ? axisNowMin : null;
  } else if (weekdayIn(store.timezone, now) === day) {
    nowMin = minutesOfDayIn(store.timezone, now);
  } else {
    nowMin = null;
  }

  const openHours = totalOpenMinutes(schedule.segments) / 60;

  // At most one note per row, saying why it has no bars — in priority order,
  // since an unknown timezone is only worth reporting on a row that would
  // otherwise have drawn something.
  let note: ReactNode = null;
  if (schedule.kind === "closed") {
    note = <RowNote>Closed</RowNote>;
  } else if (schedule.kind === "unknown") {
    note = <RowNote>Hours unknown</RowNote>;
  } else if (!placeable) {
    note = <RowNote>Unknown timezone ({store.timezone})</RowNote>;
  }

  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      <div
        style={{
          width: LABEL_WIDTH,
          flexShrink: 0,
          paddingRight: 12,
          overflow: "hidden",
        }}
      >
        <Anchor
          component={Link}
          to={`/store/${store.id}`}
          size="sm"
          title={`${store.name} · ${store.timezone}`}
          style={{
            display: "block",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {store.name}
        </Anchor>
      </div>

      <div
        style={{
          position: "relative",
          flex: 1,
          height: ROW_HEIGHT,
          borderTop: "1px solid var(--mantine-color-default-border)",
        }}
      >
        {ticks.slice(1, -1).map((hour) => (
          <div
            key={hour}
            style={{
              position: "absolute",
              left: pct(hour * 60),
              top: 0,
              bottom: 0,
              width: 1,
              background: "var(--mantine-color-default-border)",
            }}
          />
        ))}

        {segments.map((segment, index) => (
          <Tooltip
            key={index}
            withinPortal
            label={
              <BarTooltip
                store={store}
                schedule={schedule}
                segment={segment}
                axisZone={axisZone}
              />
            }
          >
            <div
              style={{
                position: "absolute",
                left: pct(segment.startMin),
                width: pct(segment.endMin - segment.startMin),
                top: 5,
                height: ROW_HEIGHT - 11,
                borderRadius: 2,
                background: "var(--mantine-color-blue-6)",
                // Hours inherited from the single default window read fainter
                // than a genuine per-day override.
                opacity: schedule.source === "per-day" ? 1 : 0.55,
              }}
            />
          </Tooltip>
        ))}

        {note}

        {nowMin !== null ? (
          <div
            title={`Now — ${formatMinutes(nowMin)} ${axisZone ?? store.timezone}`}
            style={{
              position: "absolute",
              left: pct(nowMin),
              top: 0,
              bottom: 0,
              width: 2,
              marginLeft: -1,
              background: "var(--mantine-color-red-6)",
              zIndex: 3,
              pointerEvents: "none",
            }}
          />
        ) : null}

        {schedule.kind === "windows" ? (
          <Text
            size="xs"
            c="dimmed"
            style={{
              position: "absolute",
              right: 4,
              top: 5,
              pointerEvents: "none",
            }}
          >
            {formatHours(openHours)}
          </Text>
        ) : null}
      </div>
    </div>
  );
}

function RowNote({ children }: { children: ReactNode }) {
  return (
    <Text
      size="xs"
      c="dimmed"
      style={{ position: "absolute", left: 6, top: 5 }}
    >
      {children}
    </Text>
  );
}

interface BarTooltipProps {
  store: StoreRecord;
  schedule: DaySchedule;
  segment: Segment;
  axisZone: AxisZone;
}

function BarTooltip({ store, schedule, segment, axisZone }: BarTooltipProps) {
  return (
    <div>
      <Text size="xs" fw={600}>
        {store.name}
      </Text>
      <Text size="xs">
        {formatMinutes(segment.startMin)} – {formatMinutes(segment.endMin)}{" "}
        {axisZone ?? store.timezone}
      </Text>
      {axisZone && axisZone !== store.timezone ? (
        <Text size="xs" c="dimmed">
          Store local: {schedule.segments.map(describeSegment).join(", ")}{" "}
          {store.timezone}
        </Text>
      ) : null}
      <Text size="xs" c="dimmed">
        {schedule.source === "per-day"
          ? "Per-day override"
          : "Default window — no per-day hours set"}
      </Text>
    </div>
  );
}

function describeSegment(segment: Segment): string {
  return `${formatMinutes(segment.startMin)}–${formatMinutes(segment.endMin)}`;
}

function formatHours(value: number): string {
  return Number.isInteger(value) ? `${value}h` : `${value.toFixed(1)}h`;
}

export function TimelineLegend({ axisZone }: { axisZone: AxisZone }) {
  return (
    <Group gap="lg">
      <Swatch color="var(--mantine-color-blue-6)" label="Per-day hours" />
      <Swatch
        color="var(--mantine-color-blue-6)"
        opacity={0.55}
        label="Default window"
      />
      <Swatch color="var(--mantine-color-red-6)" label="Now" width={3} />
      <Text size="xs" c="dimmed">
        {axisZone ? `Axis: ${axisZone}` : "Axis: each store's local time"}
      </Text>
    </Group>
  );
}

function Swatch({
  color,
  label,
  opacity = 1,
  width = 12,
}: {
  color: string;
  label: string;
  opacity?: number;
  width?: number;
}) {
  return (
    <Group gap={6} wrap="nowrap">
      <span
        style={{
          display: "inline-block",
          width,
          height: 12,
          background: color,
          opacity,
          borderRadius: 2,
        }}
      />
      <Text size="xs" c="dimmed">
        {label}
      </Text>
    </Group>
  );
}
